import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { NotificationService } from "../../api/generated/services/NotificationService";
import {
  getNotificationDeliveryConversionForAdmin,
  NotificationDeliveryConversionViewModel,
  NotificationDeliveryHistoryRow,
} from "../../api/segmentedPush";
import { Button } from "../Button";
import { Loader } from "../Loader";
import "../../api/openApiConfig";

const PAGE_SIZE = 25;

interface DeliveryHistoryModalProps {
  isOpen: boolean;
  notificationId: string | null;
  notificationName: string | null;
  onClose: () => void;
}

type ConversionState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; data: NotificationDeliveryConversionViewModel };

const stateBadgeClass = (state: string | null | undefined): string => {
  switch (state) {
    case "Success":
      return "bg-green-100 text-green-800";
    case "Fail":
      return "bg-red-100 text-red-800";
    case "InProgress":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString("da-DK") : "—";

// The backend description reads "Sent: N, Failed: M"; failed sends are almost always
// stale device tokens, so only the delivered count is worth an admin's attention
const formatResult = (description: string | null | undefined): string => {
  const match = /Sent:\s*(\d+)/.exec(description ?? "");
  return match ? `Sent: ${match[1]}` : description ?? "—";
};

const ConversionCell: React.FC<{
  deliveryId: string | null | undefined;
  conversion: ConversionState | undefined;
  onLoad: (deliveryId: string) => void;
}> = ({ deliveryId, conversion, onLoad }) => {
  if (!deliveryId) {
    return (
      <span className="text-xs text-gray-400">
        Not tracked (sent before recipient tracking)
      </span>
    );
  }
  if (!conversion) {
    return (
      <Button variant="outline" size="sm" onClick={() => onLoad(deliveryId)}>
        Show
      </Button>
    );
  }
  if (conversion.status === "loading") {
    return <Loader />;
  }
  if (conversion.status === "error") {
    return <span className="text-xs text-red-800">Failed to load</span>;
  }

  const { recipientsCount, convertedCount, convertedWithin7DaysCount, convertedWithin30DaysCount } =
    conversion.data;
  if (recipientsCount === 0) {
    return <span className="text-xs text-gray-400">No recipients recorded</span>;
  }
  return (
    <div className="text-xs">
      <div>
        {convertedCount} of {recipientsCount} purchased (
        {((convertedCount / recipientsCount) * 100).toFixed(1)}%)
      </div>
      <div className="text-gray-400">
        within 7d: {convertedWithin7DaysCount} · within 30d:{" "}
        {convertedWithin30DaysCount}
      </div>
    </div>
  );
};

export const DeliveryHistoryModal: React.FC<DeliveryHistoryModalProps> = ({
  isOpen,
  notificationId,
  notificationName,
  onClose,
}) => {
  const [deliveries, setDeliveries] = useState<NotificationDeliveryHistoryRow[]>(
    []
  );
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversions, setConversions] = useState<
    Record<string, ConversionState>
  >({});

  useEffect(() => {
    if (!isOpen || !notificationId) return;

    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response =
          await NotificationService.postApiNotificationListNotificationDeliveryHistory(
            {
              notificationId,
              pagination: { pageNumber: pageNumber - 1, pageSize: PAGE_SIZE },
            }
          );
        if (ignore) return;
        setDeliveries((response.data as NotificationDeliveryHistoryRow[]) ?? []);
        setTotal(response.total ?? 0);
        setError(null);
      } catch (err) {
        if (ignore) return;
        console.error("Error fetching delivery history:", err);
        setError("Failed to fetch the delivery history.");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isOpen, notificationId, pageNumber]);

  useEffect(() => {
    if (isOpen) {
      setPageNumber(1);
      setConversions({});
    }
  }, [isOpen, notificationId]);

  const loadConversion = async (deliveryId: string) => {
    setConversions((previous) => ({
      ...previous,
      [deliveryId]: { status: "loading" },
    }));
    try {
      const data = await getNotificationDeliveryConversionForAdmin(deliveryId);
      setConversions((previous) => ({
        ...previous,
        [deliveryId]: { status: "loaded", data },
      }));
    } catch {
      setConversions((previous) => ({
        ...previous,
        [deliveryId]: { status: "error" },
      }));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-content md:max-w-[900px]"
      overlayClassName="modal-overlay"
      ariaHideApp={false}
    >
      <h3 className="text-lg font-medium mb-1">Delivery history</h3>
      {notificationName && (
        <p className="text-sm text-gray-400 mb-4">{notificationName}</p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      ) : deliveries.length === 0 ? (
        <p className="text-sm text-gray-400 my-8 text-center">
          This notification has not been sent yet.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Sent</th>
                  <th>State</th>
                  <th>Result</th>
                  <th>Conversion</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((delivery, index) => (
                  <tr key={delivery.id ?? index}>
                    <td>{formatDate(delivery.createdAt)}</td>
                    <td>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stateBadgeClass(
                          delivery.state
                        )}`}
                      >
                        {delivery.state ?? "Unknown"}
                      </span>
                    </td>
                    <td className="text-sm">{formatResult(delivery.description)}</td>
                    <td>
                      <ConversionCell
                        deliveryId={delivery.id}
                        conversion={
                          delivery.id ? conversions[delivery.id] : undefined
                        }
                        onLoad={loadConversion}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Conversion counts every recipient with a cashback-earning purchase
            after the send — correlation, not attribution: the push may not be
            what caused the purchase.
          </p>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <Button
                variant="outline"
                disabled={pageNumber === 1}
                onClick={() => setPageNumber((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {pageNumber} of {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={pageNumber === totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end mt-4">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};
